import {
  enumerateOneOfKeys,
  followRef,
  getRelationship,
  isUnionSchema,
  type ProjectSchemaJSON,
  type PropertySchema
} from '@takeshape/schema';
import keyBy from 'lodash/keyBy';
import _set from 'lodash/set';
import type { Content } from '../../../content/types';

export type ContentById = Record<string, Content>;

export type NormalizedContentById = Record<string, Content | ContentById>;

const getStore =
  (getIsSingle: (shapeId: string) => boolean) =>
  (obj: NormalizedContentById, data: Content): void => {
    const { _shapeId, _id } = data;
    const isSingle = getIsSingle(_shapeId);
    Object.keys(data).forEach((k) => {
      const value = data[k];
      if (value !== undefined && value !== null) {
        const path = isSingle ? [_shapeId, k] : [_shapeId, _id, k];
        _set(obj, path, value);
      }
    });
  };

type Id = {
  shapeId: string;
  id: string;
};

const getReference = (value: Content): Id => ({
  shapeId: value._shapeId,
  id: value._id
});

function isObject(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object';
}

const removePrefixFromKey = (prefixedKey: string, shapeName?: string): string => {
  if (!shapeName) {
    return prefixedKey;
  }

  const prefix = `${shapeName}_`;
  return prefixedKey.startsWith(prefix) ? prefixedKey.substring(prefix.length) : prefixedKey;
};

export default function normalizeResponse(data: Content, projectSchema: ProjectSchemaJSON): NormalizedContentById {
  const shapesById = keyBy(Object.values(projectSchema.shapes), 'id');
  const result: NormalizedContentById = {};
  const getShape = (data: Content) => shapesById[data._shapeId];
  const store = getStore((shapeId) => Boolean(shapesById[shapeId]?.model?.type === 'single'));

  const normalize = (data: unknown, schema: PropertySchema, shapeName?: string): unknown => {
    schema = followRef(projectSchema, schema);
    if (data === undefined || data === null) {
      return;
    }

    const relationshipInfo = getRelationship(schema);
    if (relationshipInfo) {
      const processItem = (item: Content): Content => {
        const shape = getShape(item);
        // Mutates result to have a map from shapeId to content
        store(result, normalize(item, shape.schema, shape.name) as Content);
        // Returns a reference with the shapeId and the contentId
        return getReference(item);
      };

      // Handle either multiple or single relationship
      return Array.isArray(data) ? data.map(processItem) : processItem(data as Content);
    }

    if (isObject(data) && schema.type === 'object' && schema.properties) {
      const result: Content = {};
      for (const prefixedKey of Object.keys(data)) {
        const key = removePrefixFromKey(prefixedKey, shapeName);
        const propSchema = schema.properties[key];
        if (propSchema) {
          result[key] = normalize(data[prefixedKey], propSchema);
        }
      }

      return result;
    }

    if (Array.isArray(data) && schema.type === 'array' && schema.items) {
      return data.map((item) => normalize(item, schema.items!));
    }

    if (isUnionSchema(schema) && isObject(data) && typeof data.__typename === 'string') {
      const unionedShapes = enumerateOneOfKeys(projectSchema, schema.oneOf);
      const one = unionedShapes.find((shape) => shape.shapeName === data.__typename)!;
      const normalizedData = normalize(data, one.propSchema, one.shapeName) as Record<string, unknown>;

      return {
        ...normalizedData,
        __typename: data.__typename,
        _shapeId: projectSchema.shapes[data.__typename].id
      };
    }

    return data;
  };

  const topLevel = normalize(data, getShape(data).schema);
  store(result, topLevel as Content);
  return result;
}
